import { RefObject, useState } from "react";
import jsPDF from "jspdf";
import exportFromJSON from "export-from-json";
import { CPTType, ICDType } from "../../codingModule/type/normalizeCodesForUi";

const sortCptByConfidence = (a: CPTType, b: CPTType) =>
  parseInt(b.cpt_confidence) - parseInt(a.cpt_confidence);

const sortIcdByConfidence = (a: ICDType, b: ICDType) =>
  parseInt(b.icd_confidence) - parseInt(a.icd_confidence);

const transformCptToRow = (cpt: CPTType, type?: string) => {
  const cptReason = cpt.cpt_voice_note_quotes?.length
    ? `${cpt.cpt_rationale} ${cpt.cpt_voice_note_quotes
        .map((q: string) => `"${q}"`)
        .join(" ")}`
    : cpt.cpt_rationale;

  return {
    "Code Type": type === "HCPCS" ? "HCPCS" : "CPT",
    Code: cpt.cpt_code,
    Description: cpt.cpt_description,
    Confidence: cpt.cpt_confidence,
    Reason: cptReason,
  };
};

const transformIcdToRow = (icd: ICDType) => {
  const icdReason = icd.icd_voice_note_quotes?.length
    ? `${icd.icd_rationale} ${icd.icd_voice_note_quotes
        .map((q: string) => `"${q}"`)
        .join(" ")}`
    : icd.icd_rationale;

  return {
    "Code Type": "ICD-10",
    Code: icd.icd_code,
    Description: icd.icd_description,
    Confidence: icd.icd_confidence,
    Reason: icdReason,
  };
};

const buildCptRows = (list: CPTType[]) => {
  const sortedCpts = [...list];
  sortedCpts.sort(sortCptByConfidence);

  return sortedCpts.flatMap((item) => {
    const rows = [transformCptToRow(item, "CPT")];

    const sortedIcds = [...(item.associated_icds ?? [])];
    sortedIcds.sort(sortIcdByConfidence);

    sortedIcds.forEach((icd) => {
      rows.push(transformIcdToRow(icd));
    });

    return rows;
  });
};

const buildHcpcsRows = (list: CPTType[]) => {
  const sortedHcpcs = [...list];
  sortedHcpcs.sort(sortCptByConfidence);

  return sortedHcpcs.map((item) => transformCptToRow(item, "HCPCS"));
};

const exportCsv = (rows: unknown[]) =>
  exportFromJSON({
    data: rows,
    fileName: "CaseCPT_Clustered",
    exportType: "csv",
  });

const savePdfFromImage = (image: HTMLImageElement, imgSrc: string) => {
  const a4Width = 595.28;
  const a4Height = 841.89;
  const pxToPt = 0.75;
  const scaledImgWidth = image.width * pxToPt;
  const scaledImgHeight = image.height * pxToPt;
  const scale = a4Width / scaledImgWidth;
  const finalHeight = scaledImgHeight * scale;
  const pdfHeight = Math.max(a4Height, finalHeight);
  const pdf = new jsPDF("portrait", "pt", [a4Width, pdfHeight]);

  pdf.addImage(imgSrc, "PNG", 0, 0, a4Width, finalHeight);
  pdf.save("case-cpt-export.pdf");
};

type UseCaseCodesExportArgs = {
  containerRef: RefObject<HTMLElement>;
  takeScreenshot: (
    node: HTMLElement,
    options?: Record<string, unknown>,
  ) => Promise<string>;
  hasAnyCodes: boolean;
  cptData: CPTType[];
  hcpcsData: CPTType[];
  apiType?: string;
  isBoth: boolean;
  clearExpandedIndices: () => void;
};

export default function useCaseCodesExport({
  containerRef,
  takeScreenshot,
  hasAnyCodes,
  cptData,
  hcpcsData,
  apiType,
  isBoth,
  clearExpandedIndices,
}: UseCaseCodesExportArgs) {
  const [forceExpandAll, setForceExpandAll] = useState(false);

  const handleExportScreenshot = async () => {
    if (!containerRef.current || !hasAnyCodes) return;

    setForceExpandAll(true);
    await new Promise((r) => requestAnimationFrame(r));

    const img = await takeScreenshot(containerRef.current, {
      backgroundColor: "#fff",
      useCORS: true,
    });

    const image = new Image();
    image.src = img;

    image.onload = () => {
      savePdfFromImage(image, img);
      setForceExpandAll(false);
      clearExpandedIndices();
    };

    image.onerror = () => {
      setForceExpandAll(false);
    };
  };

  const handleExportCsv = () => {
    if (!hasAnyCodes) return;

    if (apiType === "HCPCS") {
      exportCsv(buildHcpcsRows(hcpcsData));
      return;
    }

    if (isBoth) {
      exportCsv([...buildCptRows(cptData), ...buildHcpcsRows(hcpcsData)]);
      return;
    }

    exportCsv(buildCptRows(cptData));
  };

  return { forceExpandAll, handleExportScreenshot, handleExportCsv };
}
